import { useToast } from '@chakra-ui/react'
import type React from 'react'
import { useEffect, useState } from 'react'
import CDropdownMenu from '../../../../../../components/CDropdownMenu'
import DefaultModal from '../../../../../../components/DefaultModal'
import DropdownCell from '../../../../../../components/DropdownCell'
import Toast from '../../../../../../components/Toast'
import {
  useAssignUserFeeStructureMutation,
  useGetFeeStructuresQuery,
  useGetUserFeeStructureQuery,
} from '../../../../../../redux/services/coreApi'
import type {
  FeeStructureDto,
  UserDto,
} from '../../../../../../types/coreApi-types'

interface AssignFeeStructureModalProps {
  isOpen: boolean
  onClose: () => void
  user?: UserDto
}

const AssignFeeStructureModal: React.FC<AssignFeeStructureModalProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const toast = useToast()
  const [selectedFeeStructure, setSelectedFeeStructure] =
    useState<FeeStructureDto | null>(null)

  const { data: feeStructures, isLoading: isLoadingFeeStructures } =
    useGetFeeStructuresQuery()

  const { data: currentFeeStructure, isLoading: isLoadingCurrentFeeStructure } =
    useGetUserFeeStructureQuery(user?.id ?? '', {
      skip: !user?.id || !isOpen,
    })

  const [assignFeeStructure, { isLoading: isAssigning }] =
    useAssignUserFeeStructureMutation()

  useEffect(() => {
    if (isOpen && currentFeeStructure) {
      setSelectedFeeStructure(currentFeeStructure)
    } else if (isOpen && !currentFeeStructure) {
      setSelectedFeeStructure(null)
    }
  }, [isOpen, currentFeeStructure])

  const handleAssign = async () => {
    if (!user?.id || !selectedFeeStructure) return

    try {
      await assignFeeStructure({
        userId: user.id,
        feeStructureId: selectedFeeStructure.id,
      }).unwrap()

      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="success">Fee structure assigned successfully.</Toast>
        ),
      })

      onClose()
    } catch (err: any) {
      const errorMsg =
        err?.data?.errors || 'Failed to assign fee structure. Please try again.'

      toast({
        position: 'bottom-right',
        render: () => <Toast type="error">{errorMsg}</Toast>,
      })
    }
  }

  const isLoading = isLoadingFeeStructures || isLoadingCurrentFeeStructure

  return (
    <DefaultModal
      isLoading={isLoading || isAssigning}
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Fee Structure"
      isLoadingText={
        isLoading ? 'Loading fee structures...' : 'Assigning fee structure...'
      }
      whiteBG
    >
      <div className="p-5 space-y-4">
        <div>
          <p className="text-description mb-4">
            Select a fee structure to assign to {user?.name || 'this user'}.
          </p>

          <CDropdownMenu
            label="Fee Structure"
            emptyText="Select a fee structure"
            selectedText={selectedFeeStructure?.description}
            renderList={
              <>
                {feeStructures?.map(feeStructure => (
                  <DropdownCell
                    key={feeStructure.id}
                    title={feeStructure.description}
                    subtitle={
                      feeStructure.thirtyDayRolling
                        ? '30-day rolling window'
                        : 'Standard window'
                    }
                    isActive={selectedFeeStructure?.id === feeStructure.id}
                    onClick={() => setSelectedFeeStructure(feeStructure)}
                  />
                ))}
              </>
            }
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            className="button-secondary"
            onClick={onClose}
            disabled={isAssigning}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button"
            onClick={handleAssign}
            disabled={!selectedFeeStructure || isAssigning}
          >
            Assign Fee Structure
          </button>
        </div>
      </div>
    </DefaultModal>
  )
}

export default AssignFeeStructureModal
